import { ProxiedCopilotRuntimeAgent } from '@copilotkit/core'
import { EventType, type BaseEvent, type RunAgentInput } from '@ag-ui/client'
import type { Message } from '@copilotkit/shared'
import { Observable } from 'rxjs'

interface ResumableRuntimeAgentConfig {
    runtimeUrl: string
    // Distinct LOCAL registry id (must NOT collide with a runtime-discovered
    // agent id, or `remoteAgents` overwrites this instance in the registry).
    agentId: string
    // The runtime's agent id that runs are proxied to (routedId).
    runtimeAgentId: string
    // Loads durable thread state from our REST endpoints: history
    // (/message_snapshot, with the trailing unanswered HITL call still open) and
    // agent state (/state_snapshot). Both are restored on reconnect so this is
    // the single restore source (no separate setMessages/setState bridge).
    loadSnapshot: (
        threadId: string,
        opts?: { waitForRun?: boolean }
    ) => Promise<{ messages: Message[]; state?: Record<string, unknown> }>
}

// CopilotKit's runtime agent, but with a durable RECONNECT.
//
// On reload, CopilotKit's in-memory runner replays its ephemeral, per-process
// event cache — which mangles a pending HITL call's args (JSON.parse crash) or
// drops them (degenerate widget), because nothing durable backs the connect.
// Here we override `connect` to instead emit a MESSAGES_SNAPSHOT built from our
// durable ADK-backed /message_snapshot. The pending `ask_user_choice` arrives
// with valid args and no tool result, so CopilotKit's native flow
// (processAgentResult -> executeSpecificTool -> useHumanInTheLoop) renders it as
// a live, answerable widget. `run()` is inherited unchanged, so live turns and
// the HITL resume still proxy through the runtime.
export class ResumableRuntimeAgent extends ProxiedCopilotRuntimeAgent {
    private readonly loadSnapshot: ResumableRuntimeAgentConfig['loadSnapshot']

    constructor({
        runtimeUrl,
        agentId,
        runtimeAgentId,
        loadSnapshot,
    }: ResumableRuntimeAgentConfig) {
        // Match the runtime's single-endpoint transport (runs POST the
        // method-envelope to /copilotkit). Runtime-discovered agents get this
        // from the /info sync; a self-managed agent must set it explicitly, or
        // run() posts to the multi route and the runtime 400s ("Missing method
        // field").
        super({ runtimeUrl, agentId, runtimeAgentId, transport: 'single' })
        this.loadSnapshot = loadSnapshot
    }

    override connect(input: RunAgentInput): Observable<BaseEvent> {
        const { threadId, runId } = input
        return new Observable<BaseEvent>((subscriber) => {
            let cancelled = false

            const emit = ({
                messages,
                state,
            }: {
                messages: Message[]
                state?: Record<string, unknown>
            }) => {
                if (state && Object.keys(state).length > 0) {
                    subscriber.next({
                        type: EventType.STATE_SNAPSHOT,
                        snapshot: state,
                    } as unknown as BaseEvent)
                }
                subscriber.next({
                    type: EventType.MESSAGES_SNAPSHOT,
                    messages,
                } as unknown as BaseEvent)
            }

            const run = async () => {
                subscriber.next({
                    type: EventType.RUN_STARTED,
                    threadId,
                    runId,
                } as BaseEvent)
                // 1. Immediate snapshot — show current progress right away.
                const partial = await this.loadSnapshot(threadId)
                if (cancelled) return
                emit(partial)
                // 2. Settled snapshot — block (?wait) until any in-flight run
                //    pauses or completes, then re-emit if it advanced. This is
                //    how a mid-run reload finalizes (e.g. into the live HITL
                //    widget) instead of being stuck on a stale partial.
                const settled = await this.loadSnapshot(threadId, {
                    waitForRun: true,
                })
                if (cancelled) return
                // Re-emit if the run advanced — more messages OR changed agent
                // state (a tool can mutate state without adding a message).
                const advanced =
                    settled.messages.length !== partial.messages.length ||
                    JSON.stringify(settled.state) !==
                        JSON.stringify(partial.state)
                if (advanced) {
                    emit(settled)
                }
                subscriber.next({
                    type: EventType.RUN_FINISHED,
                    threadId,
                    runId,
                } as BaseEvent)
                subscriber.complete()
            }

            void run().catch((error: unknown) => {
                if (!cancelled) subscriber.error(error)
            })

            return () => {
                cancelled = true
            }
        })
    }
}
